import ParameterValidator from '@mvps-genesis/parameter-validator';

/**
 * @callback HasAnimationCallback
 * @returns {Boolean}
 */

/**
 * @typedef {Object} AnimationAwaiterOptions
 * @prop {HTMLElement|Document} container The object to run `querySelector` against.
 * @prop {HasAnimationCallback} hasAnimation
 */

/**
 * Class used to assist in "promisifying" the `animationend` event handler.
 * @class
 */
export default class AnimationAwaiter {

	/**
	 * @private
	 * @type {ParameterValidator}
	 */
	#validator;

	/**
	 * @type {HTMLElement}
	 */
	element;

	/**
	 * @constructor
	 * @param {String|HTMLElement} element A selector string or HTMLElement.
	 * @param {AnimationAwaiterOptions} options
	 */
	constructor (element, {
		container,
		hasAnimation = () => true
	} = {}) {
		this.#validator = new ParameterValidator(this);

		this.element = this.#validator.isElement('element', element, {container: container});
		this.hasAnimation = this.#validator.isFunction('hasAnimation', hasAnimation);
	}

	/**
	 * Getter to determine if the downstream script should wait for an animation to finish.
	 * @returns {Promise<Boolean>}
	 */
	get animationEnd () {
		if (this.hasAnimation()) {
			return new Promise((resolve) => {
				// Handle the animationend event
				this.element.addEventListener('animationend', () => {
					resolve(true);
				}, {once: true});

				// Handle the animationcancel event
				this.element.addEventListener('animationcancel', () => {
					resolve(false);
				}, {once: true});
			});
		}

		// Resolve if no animation is found
		return Promise.resolve(true);
	}

};
